import { Box, Grid, Typography, IconButton, Tooltip } from "@mui/joy";
import Active from "../../icons/active";
import Inactive from "../../icons/inactive";
import Suspended from "../../icons/suspended";
import { generateTransactionReceipt } from "./receiptGenerator";

// VE bank codes (SUDEBAN). For wire transfers the bank isn't stored separately — it's the
// first 4 digits of the 20-digit destination account number. PagoMóvil stores the bank name directly.
const VE_BANKS = {
    '0102': 'Banco de Venezuela', '0104': 'Venezolano de Crédito', '0105': 'Mercantil',
    '0108': 'BBVA Provincial', '0114': 'Bancaribe', '0115': 'Banco Exterior',
    '0128': 'Banco Caroní', '0134': 'Banesco', '0137': 'Sofitasa', '0138': 'Banco Plaza',
    '0146': 'Bangente', '0151': 'BFC Banco Fondo Común', '0156': '100% Banco', '0157': 'DelSur',
    '0163': 'Banco del Tesoro', '0166': 'Banco Agrícola de Venezuela', '0168': 'Bancrecer',
    '0169': 'Mi Banco', '0171': 'Banco Activo', '0172': 'Bancamiga', '0174': 'Banplus',
    '0175': 'Banco Bicentenario', '0177': 'BANFANB', '0191': 'Banco Nacional de Crédito (BNC)',
};

// Resolve the destination bank from a transaction's receiver_info.
// PagoMóvil: bank name is in BankAccount (mapped server-side) or bank_name.
// Wire: derive from the account number prefix (first 4 digits).
const resolveBancoDestino = (ri = {}) => {
    if (ri.bank_name) return ri.bank_name;
    if (ri.BankAccount && !/^\d{1,4}$/.test(String(ri.BankAccount))) return ri.BankAccount; // already a name
    const code = ri.BankAccount || String(ri.accountNumber || '').slice(0, 4);
    if (!code) return '';
    return VE_BANKS[code] ? `${code} - ${VE_BANKS[code]}` : String(code);
};

const RetryIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1 4v6h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M3.51 15a9 9 0 1 0 .49-4.95L1 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const TableTransactionsReport = ({ elements, theme, columns, isAdmin = false, showBankReference = false, onRetry, onCopyCell }) => {

    const getStatusIcon = (status) => {
        if (!status) return null;
        const lowerStatus = status.toLowerCase();

        if (lowerStatus === "success" || lowerStatus === "completed") {
            return <Active />;
        } else if (lowerStatus === "expired" || lowerStatus === "awaiting_funds_confirmation") {
            return <Suspended />;
        } else {
            return <Inactive />;
        }
    };

    // Etiquetas de estado en español (la UI está en español).
    const STATUS_LABELS = {
        success: "Completado",
        completed: "Completado",
        expired: "Cotización expirada",
        awaiting_funds_confirmation: "Procesando",
        pending: "Pendiente",
        failed: "Fallido",
    };

    const getStatusLabel = (status) => {
        if (!status) return "-";
        const lowerStatus = status.toLowerCase();
        return STATUS_LABELS[lowerStatus] || lowerStatus;
    };

    const cellStyle = {
        fontFamily: "Montserrat",
        fontSize: "13px",
        color: theme.vars.palette.text.main,
        textAlign: "center",
        wordBreak: "break-word"
    };

    // Click-to-copy: a single click on a value copies it (the parent guards against
    // copying during a horizontal drag-scroll). Shows a hover hint + pointer cursor.
    const copySx = (extra = {}) => ({
        ...cellStyle,
        ...extra,
        cursor: "pointer",
        "&:hover": { color: theme.vars.palette.primary["400"] },
    });
    const copy = (value) => () => { if (onCopyCell) onCopyCell(value); };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleString('es-ES', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleGenerateReceipt = (element) => {
        const receiptData = {
            beneficiario: element.receiver_info?.idNumberDestino || '-',
            bancoDestino: resolveBancoDestino(element.receiver_info) || '-',
            numeroId: element.external_id || '-',
            numeroTelefono: element.receiver_info?.phoneDestino || '', // empty => row omitted in the receipt
            monto: element.total_in_target_currency
                ? Number(element.total_in_target_currency).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' VES'
                : '-',
            idTransaccion: element.external_reference || '-',
            fechaHora: formatDate(element.last_updated_at)
        };

        generateTransactionReceipt(receiptData);
    };

    return (
        <>
            {elements.map((element, index) => {
                const idDest = element.receiver_info?.idNumberDestino || "-";
                const phoneDest = element.receiver_info?.phoneDestino || "-";
                const bankDest = element.receiver_info?.BankAccount || "-";
                const vesSent = element.total_in_target_currency != null
                    ? Number(element.total_in_target_currency).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    : "-";
                const fecha = element.last_updated_at ? formatDate(element.last_updated_at) : "-";
                const idBank = element.external_reference || "-";
                const bankRef = element.bank_reference || "-";
                const feeAmount = element.platform_fee_amount != null
                    ? `${Number(element.platform_fee_amount).toLocaleString(undefined, { maximumFractionDigits: 6 })} ${element.platform_fee_currency || ''}`
                    : "-";
                const muneyId = element.external_id || "-";
                const totalUsdt = element.total_quote_in_usdt != null
                    ? `${Number(element.total_quote_in_usdt).toLocaleString(undefined, { minimumFractionDigits: 6, maximumFractionDigits: 6 })} USDT`
                    : "-";

                return (
                    <Grid
                        key={index}
                        container
                        spacing={1}
                        mb={2}
                        columns={columns}
                        sx={{
                            flexGrow: 1,
                            borderRadius: "12px",
                            padding: "12px 8px",
                            backgroundColor: theme.vars.palette.card || "#FFFFFF",
                            alignItems: "center",
                            minHeight: "60px"
                        }}
                    >
                        {/* Estatus — left-aligned so the status dots line up vertically
                            regardless of label length (e.g. multi-line "Cotización expirada"). */}
                        <Grid xs={1} display="flex" justifyContent="flex-start" alignItems="center" gap={0.75} sx={{ pl: 1.5, flexWrap: 'nowrap' }}>
                            <Box sx={{ flex: '0 0 auto', display: 'flex', alignItems: 'center' }}>
                                {getStatusIcon(element.status)}
                            </Box>
                            <Typography sx={{ ...cellStyle, textAlign: "left", textTransform: "capitalize" }}>
                                {getStatusLabel(element.status)}
                            </Typography>
                        </Grid>

                        {/* Fecha */}
                        <Grid xs={1} display="flex" justifyContent="center">
                            <Typography sx={copySx()} title="Click para copiar" onClick={copy(fecha)}>
                                {fecha}
                            </Typography>
                        </Grid>

                        {/* # Identificación (Dest. Identity) */}
                        <Grid xs={1} display="flex" justifyContent="center">
                            <Typography sx={copySx()} title="Click para copiar" onClick={copy(idDest)}>
                                {idDest}
                            </Typography>
                        </Grid>

                        {/* Teléfono destino (Phone Number) */}
                        <Grid xs={1} display="flex" justifyContent="center">
                            <Typography sx={copySx()} title="Click para copiar" onClick={copy(phoneDest)}>
                                {phoneDest}
                            </Typography>
                        </Grid>

                        {/* Banco destino */}
                        <Grid xs={1} display="flex" justifyContent="center">
                            <Typography sx={copySx()} title="Click para copiar" onClick={copy(bankDest)}>
                                {bankDest}
                            </Typography>
                        </Grid>

                        {/* VES enviados (Total Target) */}
                        <Grid xs={1} display="flex" justifyContent="center">
                            <Typography sx={copySx()} title="Click para copiar" onClick={copy(vesSent)}>
                                {vesSent}
                            </Typography>
                        </Grid>

                        {/* id Banco (External Reference) */}
                        <Grid xs={1} display="flex" justifyContent="center">
                            <Typography sx={copySx({ wordBreak: "break-all" })} title="Click para copiar" onClick={copy(idBank)}>
                                {idBank}
                            </Typography>
                        </Grid>

                        {/* Referencia banco */}
                        {showBankReference && (
                            <Grid xs={1} display="flex" justifyContent="center">
                                <Typography sx={copySx({ wordBreak: "break-all" })} title="Click para copiar" onClick={copy(bankRef)}>
                                    {bankRef}
                                </Typography>
                            </Grid>
                        )}

                        {/* Columnas solo para Admin */}
                        {isAdmin && (
                            <>
                                {/* Monto comisión (Fee Amount + Fee Currency) */}
                                <Grid xs={1} display="flex" justifyContent="center">
                                    <Typography sx={copySx()} title="Click para copiar" onClick={copy(feeAmount)}>
                                        {feeAmount}
                                    </Typography>
                                </Grid>

                                {/* id Muney (External ID) */}
                                <Grid xs={1} display="flex" justifyContent="center">
                                    <Typography sx={copySx({ wordBreak: "break-all" })} title="Click para copiar" onClick={copy(muneyId)}>
                                        {muneyId}
                                    </Typography>
                                </Grid>

                                {/* Total USDt cargados */}
                                <Grid xs={1} display="flex" justifyContent="center">
                                    <Typography sx={copySx({ fontWeight: 700 })} title="Click para copiar" onClick={copy(totalUsdt)}>
                                        {totalUsdt}
                                    </Typography>
                                </Grid>
                            </>
                        )}

                        {/* Error */}
                        <Grid xs={1} display="flex" justifyContent="center" alignItems="center" gap={0.5}>
                            <Typography sx={{ ...cellStyle, color: element.error_code ? theme.vars.palette.danger[500] : theme.vars.palette.text.main }}>
                                {element.error_code || "-"}
                            </Typography>
                            {element.error_code && onRetry && (
                                <Tooltip title="Reintentar" placement="top">
                                    <IconButton
                                        size="sm"
                                        variant="soft"
                                        color="warning"
                                        onClick={() => onRetry(element)}
                                        sx={{ borderRadius: "8px", minWidth: 28, minHeight: 28 }}
                                    >
                                        <RetryIcon />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Grid>

                        {/* Recibo - Botón para generar PDF */}
                        <Grid xs={1} display="flex" justifyContent="center">
                            {
                                element.status && element.status.toLowerCase() === 'success' ? (
                                    <Tooltip title="Generar recibo PDF" placement="top">
                                        <IconButton
                                            variant="soft"
                                            color="primary"
                                            size="sm"
                                            onClick={() => handleGenerateReceipt(element)}
                                            sx={{
                                                borderRadius: "8px",
                                                '&:hover': {
                                                    backgroundColor: theme.vars.palette.primary["100"],
                                                }
                                            }}
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="20"
                                                height="20"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                                <polyline points="14 2 14 8 20 8"></polyline>
                                                <line x1="16" y1="13" x2="8" y2="13"></line>
                                                <line x1="16" y1="17" x2="8" y2="17"></line>
                                                <polyline points="10 9 9 9 8 9"></polyline>
                                            </svg>
                                        </IconButton>
                                    </Tooltip>
                                ) : (
                                    <Typography sx={{ ...cellStyle, fontWeight: 700 }}>
                                        -
                                    </Typography>
                                )
                            }
                        </Grid>

                    </Grid>
                )
            })}
        </>
    );
};

export default TableTransactionsReport;
